// DTO → view-model parsers. Every response goes through here (even though the
// wire is already camelCase) so a future server rename breaks only this file and
// consumers never touch a raw DTO.
import type {
  PortfolioStatusDto,
  PropertyDetailDto,
  PropertyDocumentDto,
  PropertyListDto,
  PropertySummaryDto,
} from './propertyContracts'
import type {
  PortfolioStatus,
  PropertyDetail,
  PropertyDocument,
  PropertyListPage,
  PropertySummary,
} from '../types/propertyTypes'

export function parsePropertySummary(dto: PropertySummaryDto): PropertySummary {
  return {
    id: dto.id,
    propertyRefCode: dto.propertyRefCode ?? null,
    propertyName: dto.propertyName,
    propertyType: dto.propertyType,
    areaName: dto.areaName ?? null,
    city: dto.city,
    priceAgreed: dto.priceAgreed ?? null,
    currencyCode: dto.currencyCode,
    propertyStatus: dto.propertyStatus,
    stage: dto.stage,
    stageStep: dto.stageStep,
    stageStepCount: dto.stageStepCount,
    offeringProcessingStatus: dto.offeringProcessingStatus ?? null,
    fundingProgressPercentage: dto.fundingProgressPercentage ?? null,
    promotedAt: dto.promotedAt ?? null,
    listingRequestId: dto.listingRequestId,
  }
}

export function parsePropertyDocument(dto: PropertyDocumentDto): PropertyDocument {
  return {
    id: dto.id,
    documentType: dto.documentType,
    documentTitle: dto.documentTitle,
    fileName: dto.fileName,
    fileSizeBytes: dto.fileSizeBytes,
    mimeType: dto.mimeType,
    documentStatus: dto.documentStatus,
    createdAt: dto.createdAt,
  }
}

export function parsePropertyDetail(dto: PropertyDetailDto): PropertyDetail {
  return {
    ...parsePropertySummary(dto),
    addressLine1: dto.addressLine1,
    addressLine2: dto.addressLine2 ?? null,
    stateProvince: dto.stateProvince ?? null,
    buildingName: dto.buildingName ?? null,
    unitNumber: dto.unitNumber ?? null,
    villaNumber: dto.villaNumber ?? null,
    landNumber: dto.landNumber ?? null,
    titleDeedNumber: dto.titleDeedNumber ?? null,
    dubaiMunicipalityNumber: dto.dubaiMunicipalityNumber ?? null,
    actualAreaSqm: dto.actualAreaSqm ?? null,
    numberOfBedrooms: dto.numberOfBedrooms ?? null,
    numberOfBathrooms: dto.numberOfBathrooms ?? null,
    floorNumber: dto.floorNumber ?? null,
    parkingSpaces: dto.parkingSpaces ?? null,
    yearBuilt: dto.yearBuilt ?? null,
    ownershipType: dto.ownershipType ?? null,
    requestRefCode: dto.requestRefCode ?? null,
    submittedAt: dto.submittedAt ?? null,
    approvedAt: dto.approvedAt ?? null,
    listedAt: dto.listedAt ?? null,
    documents: (dto.documents ?? []).map(parsePropertyDocument),
  }
}

export function parsePropertyList(dto: PropertyListDto): PropertyListPage {
  return {
    rows: dto.items.map(parsePropertySummary),
    pagination: {
      page: dto.pagination.page,
      limit: dto.pagination.limit,
      totalItems: dto.pagination.totalItems,
      totalPages: dto.pagination.totalPages,
    },
  }
}

export function parsePortfolioStatus(dto: PortfolioStatusDto): PortfolioStatus {
  return {
    totalPropertyCount: dto.totalPropertyCount,
    inReviewPropertyCount: dto.inReviewPropertyCount,
    listedPropertyCount: dto.listedPropertyCount,
    fullyFundedCount: dto.fullyFundedCount,
    liveFundingCount: dto.liveFundingCount,
    totalRaised: dto.totalRaised,
    currencyCode: dto.currencyCode,
  }
}
